"use client";

import { usePrivy, useLinkAccount } from "@privy-io/react-auth";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import { CustomPhantomModal } from "./CustomPhantomModal";
import Image from "next/image";
import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2 } from "lucide-react";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";

/**
 * Conditional wallet connection component
 * Shows different options based on device type and Phantom extension availability
 */
export function WalletConnectionOptions() {
	const { login, authenticated } = usePrivy();
	const { connect, isSignedIn } = usePrivyWallet();
	const { isMobile, hasPhantomExtension } = useDeviceAndWalletDetection();

	// Custom Phantom Modal state
	const [isCustomPhantomModalOpen, setIsCustomPhantomModalOpen] =
		useState(false);
	const [customPhantomLoading, setCustomPhantomLoading] = useState(false);
	const [customPhantomError, setCustomPhantomError] = useState<string | null>(
		null
	);

	// Stable callbacks for useLinkAccount to prevent re-render cascades
	const onLinkSuccess = useCallback(({ user, linkedAccount }: { user: any; linkedAccount: any }) => {
		setIsCustomPhantomModalOpen(false);
		setCustomPhantomLoading(false);
		setCustomPhantomError(null);

		setTimeout(() => {
			window.location.reload();
		}, 1000);
	}, []);

	const onLinkError = useCallback((error: any) => {
		const raw: string = error?.message ?? error?.error ?? String(error ?? "");
		const isAlreadyLinked =
			raw.includes("linked_to_another_user") ||
			error?.code === "linked_to_another_user";
		const isCancelled = raw.toLowerCase().includes("cancel") || raw.toLowerCase().includes("closed");

		if (!isCancelled) {
			setCustomPhantomError(
				isAlreadyLinked
					? "This wallet is already registered to another account. Go to your Profile page and use Merge Accounts to combine them."
					: `Failed to link ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
			);
		}
		setCustomPhantomLoading(false);
	}, []);

	const { linkWallet } = useLinkAccount({
		onSuccess: onLinkSuccess,
		onError: onLinkError,
	});

	const [manualPhantomCheck, setManualPhantomCheck] = useState(false);
	const [isCheckingPhantom, setIsCheckingPhantom] = useState(false);
	const [checkError, setCheckError] = useState<string | null>(null);

	const linkWalletRef = useRef(linkWallet);
	linkWalletRef.current = linkWallet;

	useEffect(() => {
		const handleWalletLinkingAfterAuth = async () => {
			const phantomAddressToLink = localStorage.getItem(
				"phantom_address_to_link"
			);

			if (authenticated && phantomAddressToLink) {
				try {
					localStorage.removeItem("phantom_address_to_link");

					const phantom = (window as any).solana;
					if (!phantom?.isPhantom) {
						throw new Error(
							`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found`
						);
					}

					await phantom.connect();

					await new Promise((resolve) => setTimeout(resolve, 1000));

					await linkWalletRef.current({
						walletList: ["phantom"] as const,
					});
				} catch (linkError: any) {
					setCustomPhantomError(
						`Failed to link ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`
					);
					setCustomPhantomLoading(false);
				}
			}
		};

		if (authenticated) {
			handleWalletLinkingAfterAuth();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authenticated]);

	// Note: Original connectPhantom function removed as we now use custom modal flow

	const createPrivyWallet = () => {
		// Use Privy's default login which will show appropriate methods based on device and Phantom availability
		connect();
	};

	// Manual phantom detection function
	const checkPhantomManually = async () => {
		setIsCheckingPhantom(true);
		setCheckError(null);

		try {
			// Wait a bit for extension to load if it's still loading
			await new Promise((resolve) => setTimeout(resolve, 500));

			const phantom = (window as any).solana;
			const isPhantomAvailable = !!(phantom && phantom.isPhantom);

			if (isPhantomAvailable) {
				setManualPhantomCheck(true);
				setCheckError(null);
			} else {
				setCheckError(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension still not detected. Please ensure it's installed and enabled.`
				);
			}
		} catch (error) {
			setCheckError(
				`Error checking for ${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension.`
			);
		} finally {
			setIsCheckingPhantom(false);
		}
	};

	// Open Custom Phantom Modal
	const openCustomPhantomModal = () => {
		setCustomPhantomError(null);
		setIsCustomPhantomModalOpen(true);
	};

	// Close Custom Phantom Modal
	const closeCustomPhantomModal = () => {
		setIsCustomPhantomModalOpen(false);
		setCustomPhantomLoading(false);
		setCustomPhantomError(null);
	};

	// Handle Custom Phantom Email Submit with wallet address
	const handleCustomPhantomEmailSubmit = async (
		email: string,
		walletAddress: string
	) => {
		setCustomPhantomLoading(true);
		setCustomPhantomError(null);

		try {
			// Call the phantom-connect API
			const response = await fetch("/api/user/phantom-connect", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					email,
					walletAddress,
				}),
			});

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.message || "Connection failed");
			}

			// Handle different response actions
			if (data.data.action === "NEEDS_WALLET_LINKING") {
				// Existing user needs to link Phantom wallet - Two-step process
				try {
					// Store the phantom address for use after login
					localStorage.setItem("phantom_address_to_link", walletAddress);

					// First, authenticate the user with Privy (email only, no wallet options)
					await login({
						loginMethods: ["email"], // Only show email login
						disableSignup: true, // Only allow existing users to log in
					});

					// Step 2 happens in useEffect after authentication
				} catch (authError: any) {
					setCustomPhantomError("Failed to log in. Please try again.");
					setCustomPhantomLoading(false);
				}
				return;
			}

			// Legacy fallback for old NEEDS_LINKING action
			if (data.data.action === "NEEDS_LINKING") {
				setCustomPhantomError(
					`Account with this email already exists. Please use "Connect Wallet" to log in first, then you can link ${MYGEOTOKENS_WALLET_DISPLAY_NAME} on your wallet page.`
				);
				return;
			}

			// Success cases (CREATED, LOGIN, or WALLET_LINKED)
			if (data.data.token) {
				// Store JWT token (you might want to use a more secure method)
				localStorage.setItem("auth_token", data.data.token);

				// Close modal on success
				setIsCustomPhantomModalOpen(false);
				setCustomPhantomLoading(false);
				setCustomPhantomError(null);

				// Sync authentication state with Privy
				try {
					// Handle different authentication scenarios
					if (data.data.action === "WALLET_LINKED_LOGIN") {
						// Wallet successfully linked - trigger Privy login to see the new wallet
						await connect();
					} else if (
						data.data.action === "LOGIN_WITH_NEW_WALLET" ||
						data.data.action === "LOGIN"
					) {
						// Use the standard Privy login for existing users
						await connect();
					} else if (data.data.action === "CREATED") {
						// For new users, they're already authenticated via our custom flow
						// Just reload to reflect the new authentication state
						window.location.reload();
					}
				} catch (authSyncError) {
					// Fallback: Just reload the page
					window.location.reload();
				}
			}
		} catch (error: any) {
			// Handle specific error types
			if (error.message?.includes("WALLET_EMAIL_CONFLICT")) {
				setCustomPhantomError(
					"This wallet is already associated with a different email address."
				);
			} else if (error.message?.includes("WALLET_ALREADY_EXISTS")) {
				setCustomPhantomError(
					"This wallet is already registered with another account."
				);
			} else {
				setCustomPhantomError(
					error.message || "Connection failed. Please try again."
				);
			}
		} finally {
			setCustomPhantomLoading(false);
		}
	};

	if (isSignedIn) return null;

	return (
		<div className="wallet-connection-container bg-[#1A1A1A] rounded-xl p-6 text-center">
			{/* Mobile: Only show Privy embedded wallet */}
			{isMobile && (
				<div className="mobile-wallet-options">
					<h3 className="text-xl font-semibold text-white mb-3">
						Get Started with MyGeoTokens
					</h3>
					<button
						onClick={createPrivyWallet}
						className="w-full bg-mgt-green py-3 px-6 rounded-xl text-black font-medium hover:bg-opacity-90 transition-colors mb-3"
					>
						Create Wallet & Start Dropping NFTs
					</button>
					<p className="text-gray-400 text-sm">
						Instant wallet creation - no downloads needed
					</p>
				</div>
			)}

			{/* Desktop: Show unified connection option */}
			{!isMobile && (
				<div className="desktop-wallet-options">
					<div className="unified-connection">
						<h3 className="text-xl font-semibold text-white mb-4">
							Get Started with MyGeoTokens
						</h3>

						<button
							onClick={createPrivyWallet}
							className="w-full bg-mgt-green py-3 px-6 rounded-xl text-black font-medium hover:bg-opacity-90 transition-colors mb-4"
						>
							Connect & Start Dropping NFTs
						</button>

						{/* Show Phantom suggestion only if Phantom is not detected */}
						{!hasPhantomExtension && !manualPhantomCheck && (
							<div className="phantom-suggestion bg-gray-800 rounded-lg p-4 mt-4">
								<p className="text-white text-sm mb-3">
									Have NFTs in an external Solana wallet?
								</p>

								<div className="space-y-2">
									<a
										href="https://phantom.app/download"
										target="_blank"
										rel="noopener noreferrer"
										className="block bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-sm text-center"
									>
										Get {MYGEOTOKENS_WALLET_DISPLAY_NAME}
									</a>

									{/* Manual check button */}
									<button
										onClick={checkPhantomManually}
										disabled={isCheckingPhantom}
										className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
									>
										{isCheckingPhantom ? (
											<>
												<Loader2 className="w-4 h-4 animate-spin" />
												Checking...
											</>
										) : (
											<>I have the extension — check again</>
										)}
									</button>
								</div>

								<p className="text-gray-400 text-xs mt-2">
									Click Check again if you just installed the extension
								</p>
							</div>
						)}

						{/* Error message for manual check */}
						{checkError && (
							<div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mt-4">
								<p className="text-red-400 text-sm">{checkError}</p>
							</div>
						)}

						<p className="text-gray-400 text-sm mt-4">
							{hasPhantomExtension || manualPhantomCheck
								? `Choose email, SMS, or connect ${MYGEOTOKENS_WALLET_DISPLAY_NAME}`
								: "Create a new wallet or connect with email/SMS"}
						</p>
					</div>
				</div>
			)}

			{/* Custom Phantom Modal */}
			<CustomPhantomModal
				isOpen={isCustomPhantomModalOpen}
				onClose={closeCustomPhantomModal}
				onEmailSubmit={handleCustomPhantomEmailSubmit}
				isLoading={customPhantomLoading}
				error={customPhantomError}
			/>
		</div>
	);
}
